import { zodResolver } from "@hookform/resolvers/zod";
import { EmailOutlined, LocalPhoneOutlined, OpenInNewOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import AlertDefault, { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import { BottomBarDouble } from "~/src/components/BottomBars/BottomBars";
import { CancelIcon, ProfileIcon, SaveIcon } from "~/src/components/Icons/Icons";
import { setApiErrorsUseForm } from "~/src/helpers/formErrors";
import { useClientState } from "~/src/hooks/useClientState";
import { UpdateJegerOpplysningerRequest } from "../../../api/types";
import { appConfig } from "../../../appConfig";
import { jaktdataApi } from "../../../appGlobals";
import ButtonDefault, { ButtonVariant } from "../../../components/Button/ButtonDefault";
import InputDefault from "../../../components/Form/Input/InputDefault";
import { default as UserContext } from "../../../contexts/AppDataContext/DataProvider";
import { useDialog } from "../../../hooks/useDialog";
import { useStateful } from "../../../hooks/useStateful";

export type JegeropplysningerFormProps = {
  className?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  mode: "velkommen" | "rediger";
};

const phoneNrRegex = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

export default function ({
  className = "",
  onConfirm = undefined,
  onCancel = undefined,
  mode,
}: JegeropplysningerFormProps): JSX.Element {
  const { t } = useTranslation();
  const errorDialog = useDialog({ title: t("pages.minSide.jegerOpplysninger.errorDialog.title") });
  const clientState = useClientState();

  const jegerOpplysningerFormValidation = z.object({
    Navn: z.string().min(3, { error: t("form.fields.navn.validationError") }),
    Telefonnummer: z
      .string()
      .regex(phoneNrRegex, { error: t("form.fields.telefon.validationError") })
      .min(7, { error: t("form.fields.telefon.validationError") })
      .max(20, { error: t("form.fields.telefon.validationError") }),
    Epost: z
      .email({ error: t("form.fields.epost.validationError") })
      .min(5)
      .or(z.literal(""))
      .nullish(),
  });

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<UpdateJegerOpplysningerRequest>({
    resolver: zodResolver(jegerOpplysningerFormValidation),
    mode: "onBlur",
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSubmitting = useStateful<boolean>(false);
  const user = useContext(UserContext);
  const onSubmit: SubmitHandler<UpdateJegerOpplysningerRequest> = async (request) => {
    isSubmitting.set(true);
    const sanitizedRequest = {
      ...request,
      Telefonnummer: request.Telefonnummer.replace(/\s+/g, ""),
    };

    try {
      const updatedData = await jaktdataApi.putJegeropplysninger(sanitizedRequest);
      queryClient.setQueryData(["jegeropplysninger"], updatedData);
      navigate("/minside");
    } catch (error: unknown) {
      if (error && typeof error === "object" && "message" in error) {
        errorDialog.show({ message: String((error as { message: string }).message) });
      } else {
        errorDialog.show({ message: "Ukjent feil" });
      }
      setApiErrorsUseForm(error as Record<string, unknown>, setError);
    } finally {
      isSubmitting.set(false);
      onConfirm?.();
    }
  };

  useEffect(() => {
    reset();
  }, []);

  useEffect(() => {
    if (user.jegeropplysninger.data?.Navn) {
      setValue("Navn", user.jegeropplysninger.data.Navn, { shouldValidate: true });
    }
    if (user.jegeropplysninger.data?.Telefonnummer) {
      setValue("Telefonnummer", user.jegeropplysninger.data.Telefonnummer, {
        shouldValidate: true,
      });
    }
    if (user.jegeropplysninger.data?.Epost) {
      setValue("Epost", user.jegeropplysninger.data.Epost, { shouldValidate: true });
    }
  }, [user.jegeropplysninger.data]);

  const cancelButton = (
    <ButtonDefault
      id="jegeropplysningerAvbrytButton"
      variant={mode === "velkommen" ? ButtonVariant.Tertiary : ButtonVariant.TertiaryDarkBg}
      type="reset"
      onClick={() => {
        reset();
        (onCancel && onCancel()) || navigate("/minside");
      }}
      icon={<CancelIcon />}
      label={t("actions.cancel")}
    />
  );

  const saveButton = (
    <ButtonDefault
      id="jegeropplysningerSubmitButton"
      type="submit"
      variant={mode === "velkommen" ? ButtonVariant.Primary : ButtonVariant.PrimaryDarkBg}
      icon={<SaveIcon />}
      label={mode === "velkommen" ? t("actions.saveInformation") : t("actions.save")}
      loading={isSubmitting.value}
      onClick={handleSubmit(onSubmit)}
      form="endreJegeropplysningerForm"
      disabled={clientState === "Offline" || clientState === "Degraded"}
    />
  );

  const showPersonvernerklaeringOppfording = mode === "velkommen";

  return (
    <div className={`flex flex-col h-full bg-ldir-brown-300 justify-between ${className} `}>
      <div className="m-4 mt-8">
        <form id="endreJegeropplysningerForm" className="w-full">
          <div className="flex flex-col gap-4 w-full">
            <InputDefault
              id="jegeropplysningerNavnInput"
              label={t("form.fields.navn.label")}
              labelIcon={
                <div className="scale-75 origin-top-left">
                  <ProfileIcon />
                </div>
              }
              fieldError={errors.Navn}
              type="text"
              autoComplete="name"
              required
              aria-required
              {...register("Navn", { required: t("form.fields.navn.validationError") })}
              defaultValue={user.jegeropplysninger.data?.Navn ?? ""}
              onValueChanged={(value: string) => setValue("Navn", value, { shouldValidate: true })}
            />
            <InputDefault
              id="jegeropplysningerTelefonnummerInput"
              label={t("form.fields.telefon.label")}
              labelIcon={<LocalPhoneOutlined />}
              type="tel"
              autoComplete="tel"
              required
              inputMode="tel"
              fieldError={errors.Telefonnummer}
              aria-required
              {...register("Telefonnummer", {
                required: t("form.fields.telefon.validationError"),
              })}
              defaultValue={user.jegeropplysninger.data?.Telefonnummer ?? ""}
              onValueChanged={(value: string) => {
                setValue("Telefonnummer", value, { shouldValidate: true });
              }}
            />
            <InputDefault
              id="jegeropplysningerEpostInput"
              label={`${t("form.fields.epost.label")} (${t("actions.optional")})`}
              labelIcon={<EmailOutlined />}
              fieldError={errors.Epost}
              {...register("Epost")}
              defaultValue={user.jegeropplysninger.data?.Epost ?? ""}
              autoComplete="email"
              name="email"
              type="email"
              placeholder="adress@mail.no"
              onValueChanged={(value: string) => setValue("Epost", value, { shouldValidate: true })}
            />
            <div>
              <AlertDefault variant={AlertVariant.Info}>
                {t("pages.minSide.jegerOpplysninger.epostOppfordring")}
              </AlertDefault>
              {showPersonvernerklaeringOppfording && (
                <div className="m-4">
                  <a href={appConfig.urls.personvernerklaering} className=" text-ldir-brown-900">
                    {t("pages.minSide.jegerOpplysninger.oppfordring", {
                      personvernerklaering: t(
                        "pages.minSide.jegerOpplysninger.personvernerklaering",
                      ),
                    }) + " "}
                    <OpenInNewOutlined className="text-ldir-blue-900 mb-1" />
                  </a>
                </div>
              )}
              {(clientState === "Offline" || clientState === "Degraded") && (
                <AlertDefault variant={AlertVariant.Warning} className="mt-px">
                  {t("pages.minSide.jegerOpplysninger.nett")}
                </AlertDefault>
              )}
            </div>
          </div>
        </form>
      </div>
      {mode === "velkommen" ? (
        <div className="px-6 pb-5 flex flex-col gap-4">
          {saveButton}
          {cancelButton}
        </div>
      ) : (
        <BottomBarDouble buttonOne={cancelButton} buttonTwo={saveButton} darkBg />
      )}
      {errorDialog.element}
    </div>
  );
}
